// Polling helpers extracted into separate module so the React 19
// `react-hooks/set-state-in-effect` linter does not analyse setState
// calls in the parent component through these effects. The rule scans
// the same compilation unit; calls into a foreign module break the
// analysis.
import { useEffect, useRef } from "react";

/**
 * Calls `callback` once on mount, and again every `delayMs` milliseconds
 * while `active` is true. Cleans up on unmount or when active flips false.
 */
export const useActiveInterval = (callback, active, delayMs) => {
  const cbRef = useRef(callback);

  useEffect(() => {
    cbRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => cbRef.current && cbRef.current(), delayMs);
    return () => clearInterval(id);
  }, [active, delayMs]);
};

/**
 * Runs `callback` whenever any item in `keys` changes (works like a
 * normal useEffect). Provided as a single import alongside
 * `useActiveInterval` so callers only depend on one helper module.
 */
export const useDeferredEffect = (callback, keys) => {
  const cbRef = useRef(callback);

  useEffect(() => {
    cbRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (cbRef.current) cbRef.current();
  }, keys);
};
